import { useCallback, useMemo, useState } from "react";
import { Input } from "@project/components/ui/input";
import { Button } from "@project/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@project/components/ui/table";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "@project/admin/components/searchable-select";
import { Plus, Trash2, Users2 } from "lucide-react";
import {
  createFamilyMemberRow,
  createFamilyMemberRows,
  type FamilyMember,
} from "@project/admin/components/family-details-constants";
import { MobileNumberField } from "@project/components/mobile-number-field";
import { DEFAULT_PHONE_COUNTRY } from "@project/lib/mobile-number";

type FamilyColumn = Exclude<keyof FamilyMember, "rowId" | "mobileCountry">;

const defaultColumns: FamilyColumn[] = [
  "name",
  "relationship",
  "mobile",
  "address",
  "occupation",
  "income",
  "emergencyContact",
];

const labels: Record<FamilyColumn, string> = {
  name: "Name",
  relationship: "Relationship",
  mobile: "Mobile Number",
  address: "Address",
  occupation: "Occupation",
  income: "Income",
  emergencyContact: "Emergency Contact",
};

/**
 * Reusable Family Details section.
 * Used inside admission, inquiry, recommendation and resident registration forms.
 */
export function FamilyDetails({
  title = "Family Details",
  subtitle = "Add family members linked to this record",
  columns = defaultColumns,
  initialRows = 3,
  embedded = false,
  value,
  onChange,
  onSearchRelationship,
}: {
  title?: string;
  subtitle?: string;
  columns?: FamilyColumn[];
  initialRows?: number;
  embedded?: boolean;
  value?: FamilyMember[];
  onChange?: (rows: FamilyMember[]) => void;
  onSearchRelationship?: (query: string) => Promise<SearchableSelectOption[]>;
}) {
  const [internalRows, setInternalRows] = useState<FamilyMember[]>(() =>
    createFamilyMemberRows(initialRows)
  );
  const rows = value ?? internalRows;
  const visibleColumns = useMemo(
    () => defaultColumns.filter(column => columns.includes(column)),
    [columns]
  );

  const setRows = useCallback(
    (updater: FamilyMember[] | ((prev: FamilyMember[]) => FamilyMember[])) => {
      if (onChange) {
        const prev = value ?? internalRows;
        onChange(typeof updater === "function" ? updater(prev) : updater);
      } else {
        setInternalRows(updater);
      }
    },
    [onChange, value, internalRows]
  );

  const update = (i: number, patch: Partial<FamilyMember>) =>
    setRows(r => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));

  const renderCell = (row: FamilyMember, i: number, column: FamilyColumn) => {
    if (column === "emergencyContact") {
      return (
        <div className="flex min-w-36 items-center justify-center">
          <input
            type="checkbox"
            checked={row.emergencyContact}
            onChange={e => update(i, { emergencyContact: e.target.checked })}
            className="h-4 w-4 rounded border-border"
            aria-label={`Emergency contact for member ${i + 1}`}
          />
        </div>
      );
    }

    if (column === "relationship" && onSearchRelationship) {
      return (
        <SearchableSelect
          value={row.relationship}
          onValueChange={v => update(i, { relationship: v })}
          onSearch={onSearchRelationship}
          placeholder="Select relationship..."
          searchPlaceholder="Search relationship..."
          emptyText="No relationship found."
          className="min-w-36"
        />
      );
    }

    if (column === "mobile") {
      return (
        <MobileNumberField
          value={row.mobile}
          country={row.mobileCountry ?? DEFAULT_PHONE_COUNTRY}
          onCountryChange={code => update(i, { mobileCountry: code })}
          onChange={value => update(i, { mobile: value })}
          className="min-w-52"
        />
      );
    }

    const inputType = column === "income" ? "number" : "text";

    return (
      <Input
        type={inputType}
        value={String(row[column] ?? "")}
        onChange={e =>
          update(i, { [column]: e.target.value } as Partial<FamilyMember>)
        }
        aria-label={`${labels[column]} for member ${i + 1}`}
        className="min-w-36"
      />
    );
  };

  const table = (
    <>
      <div
        className={`flex items-start justify-between gap-2 ${embedded ? "mb-4" : "mb-4"}`}
      >
        {!embedded && (
          <div className="flex items-center gap-2">
            <div className="bg-primary/10 grid h-9 w-9 place-items-center rounded-lg text-primary">
              <Users2 className="h-4 w-4" />
            </div>
            <div>
              <h3 className="text-base font-bold text-foreground">{title}</h3>
              <p className="text-xs text-muted-foreground">{subtitle}</p>
            </div>
          </div>
        )}
        <Button
          type="button"
          size="sm"
          variant="outline"
          className={embedded ? "ml-auto rounded-xl" : ""}
          onClick={() => setRows(r => [...r, createFamilyMemberRow()])}
        >
          <Plus className="mr-1 h-3.5 w-3.5" /> Add Member
        </Button>
      </div>

      {/* Desktop / tablet: table layout */}
      <div className="hidden overflow-x-auto rounded-xl border border-border md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">Member</TableHead>
              {visibleColumns.map(column => (
                <TableHead key={column}>{labels[column]}</TableHead>
              ))}
              <TableHead className="w-20 text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow
                key={
                  row.rowId ??
                  `family-${row.name}-${row.relationship}-${row.mobile}`
                }
              >
                <TableCell className="whitespace-nowrap text-xs font-semibold text-muted-foreground">
                  Member {i + 1}
                </TableCell>
                {visibleColumns.map(column => (
                  <TableCell key={column}>
                    {renderCell(row, i, column)}
                  </TableCell>
                ))}
                <TableCell className="text-right">
                  {rows.length > 1 && (
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      onClick={() =>
                        setRows(r => r.filter((_, idx) => idx !== i))
                      }
                      aria-label={`Remove member ${i + 1}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Mobile: stacked cards */}
      <div className="space-y-3 md:hidden">
        {rows.map((row, i) => (
          <div
            key={
              row.rowId ??
              `family-card-${row.name}-${row.relationship}-${row.mobile}`
            }
            className="rounded-xl border border-border bg-card p-4"
          >
            <div className="mb-3 flex items-center justify-between border-b border-border pb-2">
              <span className="text-sm font-bold text-foreground">
                Member {i + 1}
              </span>
              {rows.length > 1 && (
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 text-destructive"
                  onClick={() => setRows(r => r.filter((_, idx) => idx !== i))}
                  aria-label={`Remove member ${i + 1}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
            <div className="space-y-3">
              {visibleColumns.map(column =>
                column === "emergencyContact" ? (
                  <label
                    key={column}
                    className="flex items-center justify-between gap-2"
                  >
                    <span className="text-xs font-medium text-muted-foreground">
                      {labels[column]}
                    </span>
                    <input
                      type="checkbox"
                      checked={row.emergencyContact}
                      onChange={e =>
                        update(i, { emergencyContact: e.target.checked })
                      }
                      className="h-4 w-4 rounded border-border"
                      aria-label={`Emergency contact for member ${i + 1}`}
                    />
                  </label>
                ) : (
                  <div key={column}>
                    <span className="mb-1 block text-xs font-medium text-muted-foreground">
                      {labels[column]}
                    </span>
                    {renderCell(row, i, column)}
                  </div>
                )
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );

  if (embedded) {
    return <div>{table}</div>;
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
      {table}
    </div>
  );
}
